import React, { useCallback, useRef } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { StyleSheet, View, type ViewInstance } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  BodyText,
  Button,
  getCloudinaryUrl,
  Image,
  Modal,
  ModalDialogue,
  theme,
  useHovered,
  useResponsive,
} from '@sg/garnish';

import { INGREDIENT_CARD_IMAGE_WIDTH } from '../../../constants';
import { type CustomizationMachineTypes } from '../../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationOutOfStockModal = (props: Props) => {
  const {
    isVisible,
    outOfStockIngredients,
    productName,
    restaurantName,
    singleIngredientBody,
    multipleIngredientsBody,
    singleIngredientButtonTitle,
    multipleIngredientsButtonTitle,
    onRequestClose,
    onChooseNewIngredientPress,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─── Refs ────────────────────────────────────────────────────────────

  const buttonRef = useRef<ViewInstance>(null);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isHovered = useHovered(buttonRef);

  const isMissingMultipleIngredients = outOfStockIngredients.length > 1;

  const formattedBody = isMissingMultipleIngredients
    ? formatBody({
        body: multipleIngredientsBody,
        productName,
        restaurantName,
        fallback: formatMessage(messages.outOfStockBodyPlural, {
          product: productName,
          restaurant: restaurantName,
        }),
      })
    : formatBody({
        body: singleIngredientBody,
        productName,
        restaurantName,
        fallback: formatMessage(messages.outOfStockBodySingular, {
          product: productName,
          restaurant: restaurantName,
        }),
      });

  const formattedButtonTitle = isMissingMultipleIngredients
    ? (multipleIngredientsButtonTitle ?? formatMessage(messages.outOfStockCtaPlural))
    : (singleIngredientButtonTitle ?? formatMessage(messages.outOfStockCtaSingular));

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleCta = useCallback(() => {
    const firstIngredient = outOfStockIngredients[0];
    const groupId = firstIngredient.modificationGroupId;

    onRequestClose();
    onChooseNewIngredientPress?.(groupId);
  }, [outOfStockIngredients, onRequestClose, onChooseNewIngredientPress]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const buttonStyles = [styles.button, isHovered ? styles.buttonHovered : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ModalDialogue
      size="medium"
      visible={isVisible}
      accessibilityLabel={formatMessage(messages.outOfStockA11y)}
      withCloseBtn={false}
      onRequestClose={onRequestClose}
    >
      <SafeAreaView edges={match([['bottom'], undefined])}>
        <Modal.BodyText sizeMatch={['24']}>{formattedBody}</Modal.BodyText>

        <Modal.Row style={styles.content}>
          {outOfStockIngredients.map((modification) => {
            const { ingredient } = modification;

            if (!ingredient) return null;

            return <IngredientCard key={ingredient.id} ingredient={ingredient} />;
          })}
        </Modal.Row>

        <Modal.Footer withoutTopBorder>
          <Button
            ref={buttonRef}
            style={buttonStyles}
            size="large-wide"
            palette="spinachMuted"
            width="100%"
            onPress={handleCta}
          >
            {formattedButtonTitle}
          </Button>
        </Modal.Footer>
      </SafeAreaView>
    </ModalDialogue>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const IngredientCard = (props: CardProps) => {
  const { ingredient } = props;

  const ingredientAssetUrl = ingredient.asset.url;
  const ingredientImage = getCloudinaryUrl(ingredientAssetUrl, {
    crop: 'scale',
    width: INGREDIENT_CARD_IMAGE_WIDTH,
  });

  return (
    <View key={ingredient.name} style={styles.ingredient}>
      <Image source={ingredientImage} style={styles.image} />
      <BodyText sizeMatch={['18']}>{ingredient.name}</BodyText>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatBody(params: {
  body: string | undefined;
  productName: string | undefined;
  restaurantName: string | undefined;
  fallback: string;
}) {
  const { body, productName = '', restaurantName = '', fallback } = params;

  if (!body) return fallback;

  return body
    .replaceAll(DYNAMIC_PRODUCT_TEXT, productName)
    .replaceAll(DYNAMIC_RESTAURANT_TEXT, restaurantName);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const IMAGE_SIZE = 30;
const DYNAMIC_PRODUCT_TEXT = '(product)';
const DYNAMIC_RESTAURANT_TEXT = '(restaurant)';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly outOfStockA11y: NoMessageValues;
  readonly outOfStockBodySingular: {
    readonly product: MessageValue;
    readonly restaurant: MessageValue;
  };
  readonly outOfStockBodyPlural: {
    readonly product: MessageValue;
    readonly restaurant: MessageValue;
  };
  readonly outOfStockCtaSingular: NoMessageValues;
  readonly outOfStockCtaPlural: NoMessageValues;
}>({
  outOfStockA11y: {
    defaultMessage: 'Out of Stock',
    description: 'Customization | Out of Stock | A11y',
  },
  outOfStockBodySingular: {
    defaultMessage:
      'This ingredient is usually in the {product}, but {restaurant} is currently out of stock. Thanks for understanding!',
    description: 'Customization | Out of Stock | Body (Singular)',
  },
  outOfStockBodyPlural: {
    defaultMessage:
      'These ingredients are usually in the {product}, but {restaurant} is currently out of stock. Thanks for understanding!',
    description: 'Customization | Out of Stock | Body (Plural)',
  },
  outOfStockCtaSingular: {
    defaultMessage: 'Choose a new ingredient',
    description: 'Customization | Out of Stock | CTA (Singular)',
  },
  outOfStockCtaPlural: {
    defaultMessage: 'Choose new ingredients',
    description: 'Customization | Out of Stock | CTA (Plural)',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    marginBottom: theme.spacing['6'],
    gap: theme.spacing['2'],
  },
  ingredient: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  button: {
    paddingHorizontal: theme.spacing['4'],
    backgroundColor: '#dde2d5',
    borderWidth: 1,
    borderColor: theme.colors.KALE,
  },
  buttonHovered: {
    backgroundColor: '#d4d9cb',
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: IMAGE_SIZE,
    backgroundColor: theme.colors.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  outOfStockIngredients: CustomizationMachineTypes.ModificationsList;
  productName?: string;
  restaurantName?: string;
  singleIngredientBody?: string;
  multipleIngredientsBody?: string;
  singleIngredientButtonTitle?: string;
  multipleIngredientsButtonTitle?: string;
  onRequestClose: () => void;
  onChooseNewIngredientPress?: (groupId: string) => void;
};

type CardProps = {
  ingredient: NonNullable<CustomizationMachineTypes.Modification['ingredient']>;
};
